import "./index.less";
import React, { useEffect } from "react";
import { InfoCircleFilled, WarningFilled } from "@ant-design/icons";
import { Modal } from "antd";
import { useHistory } from "react-router";
import { InjectBaseModalProps } from "@/hooks/Modal/ModalStore";
import { useGlobalStore } from "@/store/hooks";
import ShopType from "@/enum/ShopType";
import OrderTag from "@/enum/OrderTag";
import WaybillTemplateType from "@/enum/WaybillTemplateType";
import LogisticsCompany from "@/enum/LogisticsCompany";
import { useOrderManagementStore } from "../../store/OrderManagementStore";
import OrderDTO from "@/dto/order/OrderDTO";
import WaybillTemplateDTO from "@/dto/template/WaybillTemplateDTO";

const STORAGE_KEY = "__TAOBAO_SHIP_BY_SF_JC_GUIDE_MODAL_2026_04_28__";

const WAYBILL_TEMPLATE_SETTING_URL = `/setting/waybillTemplate/repository?logisticsCompany=${LogisticsCompany.SF}`;

interface TaoBaoShipBySfJcGuideModalProps extends InjectBaseModalProps {}

function hasTaoBaoShipBySfJcOrder(orders: OrderDTO[] = []) {
    return orders.some((order) => order.tags?.includes(OrderTag.TAOTIAN_SHIP_BY_SF_JC));
}

function hasCainiaoSfWaybillTemplate(waybillTemplates: WaybillTemplateDTO[] = []) {
    return waybillTemplates.some(
        (template) =>
            [WaybillTemplateType.CAINIAO, WaybillTemplateType.TAOBAO_CAINIAO].includes(template.type) &&
            template.logisticsCompany === LogisticsCompany.SF,
    );
}

const TaoBaoShipBySfJcGuideModal: React.FC<TaoBaoShipBySfJcGuideModalProps> = (props) => {
    const { open, onCancel } = props;
    const history = useHistory();

    const handleClose = () => {
        localStorage.setItem(STORAGE_KEY, "1");
        onCancel();
    };

    const handleConfigure = () => {
        handleClose();
        history.push(WAYBILL_TEMPLATE_SETTING_URL);
    };

    return (
        <Modal
            className="taobao-ship-by-sf-jc-guide-modal"
            open={open}
            onCancel={handleClose}
            width={620}
            footer={null}
            title={null}
            mask={{ closable: false }}
            destroyOnHidden
            centered
        >
            <div className="taobao-ship-by-sf-jc-guide-modal__header">
                <WarningFilled className="taobao-ship-by-sf-jc-guide-modal__header-icon" />
                <span>重要提醒</span>
            </div>

            <div className="taobao-ship-by-sf-jc-guide-modal__body">
                <div className="taobao-ship-by-sf-jc-guide-modal__content">
                    <div className="taobao-ship-by-sf-jc-guide-modal__title">
                        <InfoCircleFilled className="taobao-ship-by-sf-jc-guide-modal__info-icon" />
                        <span>顺丰快递模板配置提醒</span>
                    </div>
                    <div className="taobao-ship-by-sf-jc-guide-modal__desc">
                        您的店铺已产生加价选顺丰的订单，需要使用顺丰履约发货。该订单为平台主动托管订单，您只需要按照指引配置顺丰速运快递模板取号打单后，使用对应运单发货由平台进行结算，您无需支付顺丰运费，如有疑问可咨询万象客服。
                    </div>
                </div>

                <button
                    className="taobao-ship-by-sf-jc-guide-modal__button"
                    type="button"
                    onClick={handleConfigure}
                >
                    立即配置顺丰模板
                </button>
            </div>
        </Modal>
    );
};

let show = false;

export function useTaoBaoShipBySfJcDialog(shopId: number) {
    const globalStore = useGlobalStore();
    const orderManagementStore = useOrderManagementStore();

    const shop = globalStore.shopMapping.get(shopId);
    const orders = orderManagementStore.table.orders;
    const hasOrder = hasTaoBaoShipBySfJcOrder(orders);

    useEffect(() => {
        if (show) return;
        if (localStorage.getItem(STORAGE_KEY)) return;
        if (!shopId) return;
        if (shop?.type !== ShopType.TAOBAO) return;
        if (!hasOrder) return;

        let ignore = false;

        const ensureDialog = async () => {
            try {
                await globalStore.waybillPrintStore.ensureWaybillTemplates();
            } catch {
                return;
            }

            if (ignore) return;
            if (show) return;
            if (localStorage.getItem(STORAGE_KEY)) return;
            if (hasCainiaoSfWaybillTemplate(globalStore.waybillPrintStore.waybillTemplates)) return;

            globalStore.modalStore.openModal(TaoBaoShipBySfJcGuideModal);
            show = true;
        };

        ensureDialog();

        return () => {
            ignore = true;
        };
    }, [shopId, shop?.type, hasOrder]);
}
